import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/core';
import { css, keyframes } from 'emotion';
import { STAGE_COLORS } from './QuoteProgressSegments';

const stageInterval = 520;
const idleInterval = 720;
const idleDelay = 2800;
const idleDuration = 7200;
const arrive = keyframes({
  from: { opacity: 0, transform: 'translateY(10px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
});
const pop = keyframes({
  '0%': { transform: 'scale(0.65)' },
  '70%': { transform: 'scale(1.12)' },
  '100%': { transform: 'scale(1)' },
});
const drawHorizontal = keyframes({
  from: { transform: 'scaleX(0)' },
  to: { transform: 'scaleX(1)' },
});
const drawVertical = keyframes({
  from: { transform: 'scaleY(0)' },
  to: { transform: 'scaleY(1)' },
});
const travelHorizontal = keyframes({
  '0%, 3%': { opacity: 0, transform: 'translateX(0)' },
  '6%': { opacity: 0.9 },
  '16%': { opacity: 0.9, transform: 'translateX(435%)' },
  '20%, 100%': { opacity: 0, transform: 'translateX(435%)' },
});
const travelVertical = keyframes({
  '0%, 3%': { opacity: 0, transform: 'translateY(0)' },
  '6%': { opacity: 0.9 },
  '16%': { opacity: 0.9, transform: 'translateY(435%)' },
  '20%, 100%': { opacity: 0, transform: 'translateY(435%)' },
});
const idleRing = keyframes({
  '0%': { opacity: 0, transform: 'scale(0.95)' },
  '3%': { opacity: 0.6 },
  '11%, 100%': { opacity: 0, transform: 'scale(1.4)' },
});

const stageInteraction = css({
  '@media (hover: hover)': {
    '&:hover [data-stage-marker-shell]': {
      transform: 'translateY(-3px) scale(1.12)',
      filter: 'drop-shadow(0 3px 5px rgba(160, 38, 48, 0.25))',
    },
    '&:hover [data-stage-marker-shell]::before': {
      animation: 'none',
      opacity: 0.55,
      transform: 'scale(1.25)',
    },
    '&:hover [data-stage-label], &:hover [data-stage-count]': {
      filter: 'brightness(0.78)',
    },
    '&:hover [data-testid="dashboard-stage-connector"]': {
      filter: 'brightness(1.15) drop-shadow(0 0 3px rgba(180, 38, 48, 0.4))',
    },
  },
  '&:active [data-stage-marker-shell]': { transform: 'scale(1.08)' },
  '@media (prefers-reduced-motion: reduce)': {
    '&:hover [data-stage-marker-shell], &:active [data-stage-marker-shell]': {
      transform: 'none',
    },
  },
});

const DashboardStageTimeline = ({ stages }) => (
  <Box
    as="ol"
    aria-label="Les cinq étapes du parcours"
    display="grid"
    gridTemplateColumns={{ base: '1fr', lg: 'repeat(5, minmax(0, 1fr))' }}
    m={0}
    px={0}
    py={{ base: 2, lg: 5 }}
    flex={1}
    alignItems="center"
  >
    {stages.map(({ label, value }, index) => (
      <Box
        key={label}
        as="li"
        listStyleType="none"
        position="relative"
        minW={0}
        className={stageInteraction}
      >
        {index < stages.length - 1 && (
          <Box
            data-testid="dashboard-stage-connector"
            position="absolute"
            top={{ base: '30px', lg: '29px' }}
            left={{ base: '29px', lg: '50%' }}
            w={{ base: '3px', lg: '100%' }}
            h={{ base: '100%', lg: '3px' }}
            bg={STAGE_COLORS[index]}
            aria-hidden="true"
            className={css({
              overflow: 'hidden',
              transition: 'filter 220ms ease-out',
              animation: `${drawVertical} 360ms ${index * stageInterval + 160}ms ease-out both`,
              transformOrigin: 'center top',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '-30%',
                left: 0,
                width: '100%',
                height: '30%',
                background: 'linear-gradient(to bottom, transparent, white, transparent)',
                filter: 'drop-shadow(0 0 3px rgba(180, 38, 48, 0.65))',
                animation: `${travelVertical} ${idleDuration}ms ${idleDelay +
                  index * idleInterval}ms linear infinite both`,
              },
              '@media (min-width: 62em)': {
                animationName: drawHorizontal,
                transformOrigin: 'left center',
                '&::after': {
                  top: 0,
                  left: '-30%',
                  width: '30%',
                  height: '100%',
                  background: 'linear-gradient(to right, transparent, white, transparent)',
                  animationName: travelHorizontal,
                },
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                transition: 'none',
                '&::after': { animation: 'none', opacity: 0 },
              },
            })}
          />
        )}
        <Flex
          position="relative"
          alignItems="center"
          flexDirection={{ base: 'row', lg: 'column' }}
          gridGap={3}
          px={3}
          py={3}
          className={css({
            animation: `${arrive} 420ms ${index * stageInterval}ms ease-out both`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          })}
        >
          <Box
            data-stage-marker-shell
            position="relative"
            flexShrink={0}
            className={css({
              transition: 'transform 220ms ease-out, filter 220ms ease-out',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: '-3px',
                border: '2px solid rgba(180, 38, 48, 0.55)',
                borderRadius: '50%',
                opacity: 0,
                pointerEvents: 'none',
                animation: `${idleRing} ${idleDuration}ms ${idleDelay +
                  index * idleInterval}ms ease-out infinite both`,
              },
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&::before': { animation: 'none', opacity: 0 },
              },
            })}
          >
            <Flex
              data-testid="dashboard-stage-marker"
              w="36px"
              h="36px"
              rounded="full"
              bg={STAGE_COLORS[index]}
              color={index < 3 ? 'darkGray' : 'white'}
              alignItems="center"
              justifyContent="center"
              fontWeight="bold"
              border="3px solid white"
              className={css({
                animation: `${pop} 440ms ${index * stageInterval}ms ease-out both`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              })}
            >
              {index + 1}
            </Flex>
          </Box>
          <Flex minW={0} flex={1} flexDirection="column" textAlign={{ base: 'left', lg: 'center' }}>
            <Text
              data-stage-label
              color="semiDarkGray.900"
              fontSize="sm"
              fontWeight="semibold"
              lineHeight="1.4"
              minH={{ lg: '40px' }}
              className={css({ transition: 'filter 220ms ease-out' })}
            >
              {label}
            </Text>
            <Text
              data-stage-count
              mt={3}
              color="red"
              fontFamily="heading"
              fontSize="2xl"
              fontWeight="bold"
              className={css({
                animation: `${arrive} 420ms ${index * stageInterval + 140}ms ease-out both`,
                transition: 'filter 220ms ease-out',
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                  transition: 'none',
                },
              })}
            >
              {typeof value === 'number' ? value.toLocaleString('fr-FR') : '—'}
              <Text as="span" ml={1} fontSize="xs" fontWeight="normal">
                dossier{value > 1 ? 's' : ''}
              </Text>
            </Text>
          </Flex>
        </Flex>
      </Box>
    ))}
  </Box>
);

export default DashboardStageTimeline;
